import { mat4, vec3 } from 'gl-matrix';
import { RenderObject } from '../object/RenderObject';
import { GltfAnimation } from '../animation';
import { FrameSet } from '../animation/AnimationState';

export type BoneCollisionShape = 'capsule' | 'sphere';

export interface BoneCapsule {
  jointIndex: number;
  jointName: string;
  parentJointIndex: number; // -1 for root (always sphere)
  parentJointName: string;
  radius: number;
  shape: BoneCollisionShape; // 'sphere' uses joint position only, 'capsule' spans parent→child
}

export interface BoneHitResult {
  target: BoneCollisionBody;
  boneCapsule: BoneCapsule;
  hitPoint: vec3;
  distance: number;
}

export class BoneCollisionBody {
  public renderObject: RenderObject;
  public boneCapsules: BoneCapsule[];
  public coarseRadius: number;

  constructor(renderObject: RenderObject, boneCapsules: BoneCapsule[], coarseRadius: number) {
    this.renderObject = renderObject;
    this.boneCapsules = boneCapsules;
    this.coarseRadius = coarseRadius;
  }

  /**
   * Auto-generate bone collision capsules from bind-pose bone lengths.
   * Each capsule spans from parent joint to child joint with a radius
   * based on the bone length. Manual overrides set specific radii.
   */
  static fromAnimation(
    renderObject: RenderObject,
    radiusScale: number = 0.4,
    overrides?: Map<string, { radius?: number; shape?: BoneCollisionShape }>,
  ): BoneCollisionBody {
    const anim = renderObject.animation as GltfAnimation;
    const bindPositions = anim.computeBindPosePositions();
    const capsules: BoneCapsule[] = [];

    // Pre-compute which joints have children (for default shape selection)
    const hasChildren = new Set<number>();
    for (let i = 0; i < anim.jointParentMap.length; i++) {
      const p = anim.jointParentMap[i];
      if (p >= 0) hasChildren.add(p);
    }

    for (let i = 0; i < anim.joints.length; i++) {
      const joint = anim.joints[i];
      const parentIdx = anim.jointParentMap[i];
      const override = overrides?.get(joint.name);

      let radius: number;
      if (override?.radius !== undefined) {
        radius = override.radius;
      } else if (parentIdx >= 0) {
        const dist = vec3.distance(bindPositions[i], bindPositions[parentIdx]);
        radius = dist * radiusScale * 0.5;
      } else {
        radius = renderObject.bounds.boundingSphere.radius * 0.05;
      }

      if (radius < 0.01) continue;

      // Default shape: root bones and leaf bones → sphere, others → capsule
      let shape: BoneCollisionShape;
      if (override?.shape) {
        shape = override.shape;
      } else if (parentIdx < 0 || !hasChildren.has(i)) {
        shape = 'sphere';
      } else {
        shape = 'capsule';
      }

      const parentName = parentIdx >= 0 ? anim.joints[parentIdx].name : joint.name;

      capsules.push({
        jointIndex: i,
        jointName: joint.name,
        parentJointIndex: parentIdx,
        parentJointName: parentName,
        radius,
        shape,
      });
    }

    const coarseRadius = renderObject.bounds.boundingSphere.radius;
    return new BoneCollisionBody(renderObject, capsules, coarseRadius);
  }

  /**
   * Get world-space endpoints of a bone capsule for the current animation frame.
   * For root bones (parentJointIndex < 0), both endpoints are the same (sphere).
   */
  getWorldCapsuleEndpoints(
    capsule: BoneCapsule,
    frameSet: FrameSet,
    modelMatrix: mat4,
    boneOverrides?: Map<number, mat4>,
  ): { a: vec3; b: vec3 } {
    const b = this.getWorldBonePosition(
      capsule.jointIndex,
      capsule.jointName,
      frameSet,
      modelMatrix,
      boneOverrides,
    );

    if (capsule.parentJointIndex < 0) {
      return { a: b, b };
    }

    const a = this.getWorldBonePosition(
      capsule.parentJointIndex,
      capsule.parentJointName,
      frameSet,
      modelMatrix,
      boneOverrides,
    );

    return { a, b };
  }

  private getWorldBonePosition(
    jointIndex: number,
    jointName: string,
    frameSet: FrameSet,
    modelMatrix: mat4,
    boneOverrides?: Map<number, mat4>,
  ): vec3 {
    let jm: mat4;

    if (boneOverrides?.has(jointIndex)) {
      jm = boneOverrides.get(jointIndex)!;
    } else {
      const frame = frameSet.frames[jointName];
      if (!frame) return vec3.fromValues(0, 0, 0);
      jm = frame.jointMatrix;
    }

    const localPos = vec3.fromValues(jm[12], jm[13], jm[14]);
    return vec3.transformMat4(vec3.create(), localPos, modelMatrix);
  }

}
